<template>
  <div class="wrapper">
    <div class="main">
      <div v-if="emailLoading" class="container-fluid text-center">
        <div class="row mt-5 mb-4 pt-5">
          <div class="col-md-8 ml-auto mr-auto text-center mt-5 h-100vh">
            <h1 class="title">Loading letter&hellip; <br /><spinner></spinner></h1>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="container-fluid">
          <div class="row mt-5 mb-4 pt-5">
            <div class="col-md-10 ml-auto mr-auto text-center mt-5">
              <h1 class="title">Death penalty law</h1>
              <br />
              <h4 class="desc">
                Email your MP (Preston) about Israel&rsquo;s discriminatory death
                penalty law.
              </h4>
              <p class="text-muted mt-3 mb-0">
                On Monday, 30th March 2026, the Israeli government approved a
                wholly discriminatory and politically motivated death penalty law,
                intended solely to enable the execution of Palestinians. Israel
                should be condemned for further entrenching the two-tier justice
                system that exists against Palestinians, and pressure should be
                exerted to ensure it repeals the law or faces consequences &mdash;
                like any other state would.
                <strong>It takes less than 10 seconds.</strong>
                Enter your full name below so it appears in the pre-written letter
                to Mark Hendrick, then click &ldquo;Open in email&rdquo; to send
                from your own account.
              </p>
              <br />
            </div>
          </div>
          <div class="row my-5">
            <div class="col-md-10 mx-auto">
              <div
                v-if="emailLoadError"
                class="alert alert-danger mb-3"
                role="alert"
              >
                Could not load the latest letter text from the server. Showing a
                backup version. If this persists, the host may need to allow CORS
                for openmasjid.io on deathpenalty.theoccupiedlands.com, or open
                this page directly on OpenMasjid.
              </div>
              <form
                ref="form"
                role="form"
                class="card bg-secondary p-3"
                @submit.prevent="handleSubmit"
              >
                <div class="card-body">
                  <div
                    v-if="showNameError"
                    ref="nameErrorAlert"
                    class="alert alert-warning mb-3"
                    role="alert"
                  >
                    Please enter your full name. It will appear in the letter to
                    your MP.
                  </div>
                  <div class="form-group">
                    <label>Full name <span class="text-danger">*</span></label>
                    <base-input
                      v-model="fullName"
                      alternative
                      placeholder="Your full name"
                      v-bind="nameInputBind"
                      :aria-describedby="
                        showNameError ? 'name-error-hint' : undefined
                      "
                      :aria-invalid="showNameError ? 'true' : undefined"
                    ></base-input>
                    <div
                      id="name-error-hint"
                      v-show="showNameError"
                      class="invalid-feedback d-block"
                    >
                      Please enter your full name.
                    </div>
                  </div>
                  <div class="form-group">
                    <label>Subject</label>
                    <base-input
                      v-model="subject"
                      alternative
                      placeholder="Subject"
                    ></base-input>
                  </div>
                  <div class="form-group">
                    <label>Letter</label>
                    <textarea
                      :value="letterBody"
                      class="form-control form-control-alternative"
                      rows="22"
                      readonly
                    ></textarea>
                  </div>
                  <div class="row">
                    <div class="col-md-6 ml-auto">
                      <base-button
                        nativeType="submit"
                        type="primary"
                        class="pull-right"
                      >
                        Open in email
                      </base-button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Spinner from "@/components/Spinner";

const MARK_HENDRICK_EMAIL = "mark.hendrick.mp@parliament.uk";
const MP_NAME = "Mark Hendrick";
const EMAIL_JSON_URL =
  "https://deathpenalty.theoccupiedlands.com/email.json";

const ADDRESS_CLAUSE =
  / I live at <constituent-address-line1><constituent-postcode>\./g;

const FALLBACK_SUBJECT = "The Discriminatory Israeli Death Penalty Law";

const LETTER_TEMPLATE_FALLBACK = `Dear Mark Hendrick,

My name is [Your Name], and I am one of your constituents of Preston.

I am writing to you regarding the recently approved Israeli death penalty law, which introduces a deeply discriminatory and politically driven framework that appears designed to target Palestinians specifically.

Palestinians in the West Bank are already tried in military courts, where conviction rates are exceptionally high, in part due to practices widely reported by human rights organisations, including the use of coercion, forced confessions, and ill-treatment during interrogations. This law not only reinforces that existing system, but now allows the Israeli Defence Minister to determine, on a case-by-case basis, whether an individual will be tried in a military or civilian court. Crucially, it also enables the death penalty to be imposed within the military court system.

I therefore urge you to publicly condemn this law in the strongest possible terms. Furthermore, I call on you to press the UK Government to take decisive action, including clear diplomatic pressure on Israel to repeal the law in full.

I would appreciate an urgent and clear response outlining what steps you, and the Government, intend to take in light of this development.

[Your Name]`;

function normalizeEmailBodyParagraphs(body) {
  if (!Array.isArray(body)) {
    return [];
  }
  return body
    .map((p) =>
      typeof p === "string" ? p.replace(ADDRESS_CLAUSE, "").trim() : ""
    )
    .filter(Boolean);
}

function applyLetterPlaceholders(text, displayName) {
  let out = text.replace(/<mp-name>/g, MP_NAME);
  out = out.replace(/<constituent-name>/g, displayName);
  return out;
}

export default {
  name: "DeathPenaltyLaw",
  components: { Spinner },
  bodyClass: "contact-page",
  data() {
    return {
      fullName: "",
      subject: FALLBACK_SUBJECT,
      showNameError: false,
      emailLoading: true,
      emailLoadError: false,
      emailBodyTemplate: null,
      useFetchedLetter: false,
    };
  },
  watch: {
    fullName() {
      if (this.fullName.trim()) {
        this.showNameError = false;
      }
    },
  },
  computed: {
    nameInputBind() {
      return this.showNameError ? { valid: false } : {};
    },
    letterBody() {
      const name = this.fullName.trim() || "[Your name]";
      if (
        this.useFetchedLetter &&
        this.emailBodyTemplate &&
        this.emailBodyTemplate.length
      ) {
        let text = this.emailBodyTemplate.join("\n\n");
        text = applyLetterPlaceholders(text, name);
        return `${text}\n\n${name}`;
      }
      let text = LETTER_TEMPLATE_FALLBACK;
      text = text.replace(/\[Your Name\]/g, name);
      return text;
    },
    mailtoUrl() {
      const encodedSubject = encodeURIComponent(this.subject);
      const encodedBody = encodeURIComponent(this.letterBody);
      return `mailto:${MARK_HENDRICK_EMAIL}?subject=${encodedSubject}&body=${encodedBody}`;
    },
  },
  async mounted() {
    await this.loadEmailTemplate();
  },
  methods: {
    async loadEmailTemplate() {
      this.emailLoading = true;
      this.emailLoadError = false;
      try {
        const res = await fetch(EMAIL_JSON_URL, {
          credentials: "omit",
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        if (
          !data ||
          typeof data.subject !== "string" ||
          !Array.isArray(data.body)
        ) {
          throw new Error("Invalid JSON shape");
        }
        const paragraphs = normalizeEmailBodyParagraphs(data.body);
        if (!paragraphs.length) {
          throw new Error("Empty body");
        }
        this.emailBodyTemplate = paragraphs;
        this.subject = data.subject.trim() || FALLBACK_SUBJECT;
        this.useFetchedLetter = true;
      } catch (e) {
        console.warn("Death penalty letter fetch failed:", e);
        this.emailLoadError = true;
        this.useFetchedLetter = false;
        this.emailBodyTemplate = null;
        this.subject = FALLBACK_SUBJECT;
      } finally {
        this.emailLoading = false;
      }
    },
    handleSubmit() {
      if (!this.fullName.trim()) {
        this.showNameError = true;
        this.$nextTick(() => {
          const el = this.$refs.nameErrorAlert;
          if (el && el.scrollIntoView) {
            el.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }
        });
        return;
      }
      this.openMailtoClient();
    },
    openMailtoClient() {
      const url = this.mailtoUrl;
      if (window.self !== window.top) {
        const a = document.createElement("a");
        a.setAttribute("href", url);
        a.setAttribute("target", "_top");
        a.setAttribute("rel", "noopener noreferrer");
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        window.location.href = url;
      }
    },
  },
};
</script>

<style scoped></style>
